import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { TextInputComponent } from '../../components/text-input/text-input.component';
import { ValidationService } from '../../services/validation.service';
import { GlobalService } from '../../services/global.service';
import { LoginService } from '../../services/login.service';
import { ModalComponent } from '../../components/modal/modal.component';
import { ButtonComponent } from '../../components/button/button.component';
import { UserService } from '../../services/user.service';
import { passwordMatchValidator } from '../../common/password-match-validator';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgIf, TextInputComponent, ModalComponent, ButtonComponent],
  templateUrl: './login-page.component.html'
})
export class LoginPageComponent implements OnInit, OnDestroy {

  loginForm!: FormGroup;
  forgotForm!: FormGroup;
  reset_form!: FormGroup;
  user_type = 'staff';
  password_type: 'password' | 'text' = 'password';
  mode: 'login' | 'forgot' | 'aadhaar' | 'face' | 'face_pending' | 'face_rejected' = 'login';
  forgotStep: 1 | 2 | 3 = 1;
  otpBoxes = new Array(6).fill(0);
  otpValues: string[] = ['', '', '', '', '', ''];
  otpTimer = 300; // 5 minutes = 300 sec
  otpInterval: any;
  canResendOtp = false;
  otpError = '';
  params: any = {};

  aadhaar_qr_image = '';
  aadhaar_request_id = '';
  aadhaar_polling: any = null;
  aadhaar_status: 'pending' | 'success' | 'failed' = 'pending';

  face_temp_token = '';
  face_login_response: any = null;
  is_first_login = false;
  face_loading = false;
  face_error = '';
  face_image_base64 = '';
  face_uploaded_path = '';
  camera_active = false;
  camera_stream: MediaStream | null = null;
  show_face_confirm_modal = false;
  face_complete_response: any = null;
  face_approval_pending = false;
  face_approval_loading = false;
  face_rejected_data: any = null;
  face_pending_data: any = null;
  face_is_resubmission = false;

  @ViewChild('videoEl') videoEl!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasEl') canvasEl!: ElementRef<HTMLCanvasElement>;
  @ViewChild('form_modal') form_modal: ModalComponent | undefined;

  private route_sub!: Subscription;

  constructor(private fb: FormBuilder, private route: ActivatedRoute, private router: Router, public vs: ValidationService, public gs: GlobalService, public login: LoginService, public us: UserService) { }

  ngOnInit(): void {
    this.route_sub = this.route.queryParams.subscribe(params => {
      this.user_type = params['user_type'] || 'staff';
      this.params = { ...params };
      this.createForms();
      this.setLoginValidations();
    });
  }

  createForms() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(this.vs.email_pattern), Validators.maxLength(this.vs.max_length_email)]],
      password: ['', [Validators.required]],
      roll_number: ''
    });

    this.forgotForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(this.vs.email_pattern), Validators.maxLength(this.vs.max_length_email)]],
      otp: [''],
      roll_number: '',
      Password: [''],
      new_password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(this.vs.password_complex_pattern)]],
      confirm_password: ['', [Validators.required]]
    }, { validators: passwordMatchValidator });

    this.reset_form = this.fb.group({
      email: [this.loginForm.value.email],
      new_password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(this.vs.password_complex_pattern)]],
      confirm_password: ['', [Validators.required]],
    }, { validators: passwordMatchValidator });
  }

  toggle_password() {
    this.password_type =
      this.password_type === 'password' ? 'text' : 'password';
  }

  setLoginValidations() {
    const emailCtrl = this.loginForm.get('email');
    const rollCtrl = this.loginForm.get('roll_number');

    if (this.user_type === 'student') {
      rollCtrl?.setValidators([Validators.required]);
      emailCtrl?.clearValidators();
      emailCtrl?.reset();
    } else {
      emailCtrl?.setValidators([
        Validators.required,
        Validators.pattern(this.vs.email_pattern),
        Validators.maxLength(this.vs.max_length_email)
      ]);
      rollCtrl?.clearValidators();
      rollCtrl?.reset();
    }

    emailCtrl?.updateValueAndValidity();
    rollCtrl?.updateValueAndValidity();
  }


  // ================= LOGIN =================
  async submit() {
    try {
      if (!this.loginForm.valid) return;
      const user_type = this.params?.user_type ?? 'staff';
      let payload: any = { password: this.loginForm.value.password };

      if (user_type === 'student') {
        payload.roll_number = this.loginForm.value.roll_number;
      } else {
        payload.email = this.loginForm.value.email;
      }

      const login_response = await this.login.login_user(payload);

      // Staff: backend already generated QR via Surepass
      if (login_response.message === 'aadhaar_required') {
        this.aadhaar_qr_image = login_response.data.qr_image;
        this.aadhaar_request_id = login_response.data.request_id;
        this.aadhaar_status = 'pending';
        this.mode = 'aadhaar';
        this.startAadhaarPolling();
        return;
      }

      // Staff: face verification required
      if (login_response.message === 'face_verification_required') {
        this.face_temp_token = login_response.data.token;
        this.face_login_response = login_response;
        this.is_first_login = login_response.data.first_login;
        this.mode = 'face';
        setTimeout(() => this.startCamera(), 150);
        return;
      }

      // Admin hasn't approved face yet — show pending screen
      if (login_response.message === 'verification_pending') {
        this.face_pending_data = login_response.data || {};
        this.mode = 'face_pending';
        return;
      }

      // Admin rejected face request — show rejected screen
      if (login_response.message === 'verification_rejected') {
        this.face_rejected_data = login_response.data || {};
        this.face_temp_token = login_response.data?.token || '';
        this.is_first_login = false;
        this.mode = 'face_rejected';
        return;
      }

      // First login: force password reset (only when no verification step pending)
      if (login_response.message === 'first_login_required' || login_response.data?.first_login == true) {
        this.reset_form.patchValue({ email: payload.email });
        this.form_modal?.open();
        return;
      }

      // Student: direct login
      await this.completeLogin(login_response, user_type);
    } catch (error: any) {
      console.log(error);
      this.gs.toastr_shows_function(error?.error?.message || 'Login failed', 'Error', 'error');
    }
  }

  startAadhaarPolling() {
    this.stopAadhaarPolling();
    this.aadhaar_polling = setInterval(async () => {
      try {
        const res = await this.login.aadhaar_check_status({ request_id: this.aadhaar_request_id });
        if (res.message === 'pending') return;

        this.stopAadhaarPolling();

        if (res.message === 'face_verification_required') {
          this.aadhaar_status = 'success';
          this.face_temp_token = res.data.token;
          this.face_login_response = res;
          this.is_first_login = res.data.first_login;
          this.mode = 'face';
          setTimeout(() => this.startCamera(), 150);
        } else if (res.data) {
          this.aadhaar_status = 'success';
          await this.completeLogin(res, this.params?.user_type ?? 'staff');
        } else {
          this.aadhaar_status = 'failed';
          this.gs.toastr_shows_function(res.message || 'Aadhaar verification failed', 'Error', 'error');
        }
      } catch (err: any) {
        this.stopAadhaarPolling();
        this.aadhaar_status = 'failed';
        this.gs.toastr_shows_function('Aadhaar verification failed', 'Error', 'error');
      }
    }, 5000);
  }

  stopAadhaarPolling() {
    if (this.aadhaar_polling) {
      clearInterval(this.aadhaar_polling);
      this.aadhaar_polling = null;
    }
  }

  async completeLogin(create_response: any, user_type: string) {
    if (create_response.data.first_login == true) {
      this.form_modal?.open();
      return;
    }
    this.gs.user_data = create_response.data;
    localStorage.setItem('app_user', JSON.stringify(this.gs.user_data));
    const role_data = await this.us.get(create_response.data.id);
    this.gs.user_role_data = user_type == 'staff' ? role_data.data.Role : role_data.data?.user?.Role;
    localStorage.setItem('user_role', JSON.stringify(this.gs.user_role_data));
    this.gs.university_settings = role_data.data?.University || {};
    localStorage.setItem('university_settings', JSON.stringify(this.gs.university_settings));
    this.gs.check_and_fetch_from_local_storage();
    this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
    if (this.gs.user_data.first_login === true && user_type === 'staff') {
      this.form_modal?.open();
    } else {
      const landing_page = this.gs.user_role_data?.landing_page || '/dashboard';
      this.router.navigate([landing_page]);
    }
  }

  retryAadhaar() {
    this.stopAadhaarPolling();
    this.mode = 'login';
    this.aadhaar_qr_image = '';
    this.aadhaar_request_id = '';
    this.aadhaar_status = 'pending';
  }

  async startCamera() {
    this.face_error = '';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: 'user' } });
      this.camera_stream = stream;
      this.camera_active = true;
      const video = this.videoEl?.nativeElement;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
    } catch {
      this.face_error = 'Camera access denied. Please allow camera permissions and try again.';
    }
  }

  stopCamera() {
    this.camera_stream?.getTracks().forEach(t => t.stop());
    this.camera_stream = null;
    this.camera_active = false;
  }

  captureImage() {
    const video = this.videoEl?.nativeElement;
    const canvas = this.canvasEl?.nativeElement;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0);
    }
    this.face_image_base64 = canvas.toDataURL('image/jpeg', 0.85);
    this.stopCamera();
  }

  retakeFace() {
    this.face_image_base64 = '';
    this.face_error = '';
    setTimeout(() => this.startCamera(), 150);
  }

  async submitFaceVerification() {
    if (!this.face_image_base64 || this.face_loading) return;
    this.face_loading = true;
    this.face_error = '';
    try {
      const image_path = await this.login.upload_image(this.face_image_base64, this.face_temp_token);
      this.face_uploaded_path = image_path;

      // After a rejection, skip AI and go directly to admin approval
      if (this.face_is_resubmission) {
        await this.sendForApproval();
        return;
      }

      let res: any;
      if (this.is_first_login) {
        res = await this.login.face_register(image_path, this.face_temp_token);
      } else {
        res = await this.login.face_verify(image_path, this.face_temp_token);
      }

      if (res.code === 200) {
        const complete_response = { ...this.face_login_response, message: res.message, data: { ...this.face_login_response.data, first_login: false } };
        if (this.is_first_login) {
          this.face_complete_response = complete_response;
          this.show_face_confirm_modal = true;
        } else {
          await this.completeLogin(complete_response, this.params?.user_type ?? 'staff');
        }
      } else {
        // Face mismatch — show "Send For Approval" UI instead of error
        this.face_approval_pending = true;
      }
    } catch (error: any) {
      if (error?.status === 401) {
        this.face_approval_pending = true;
      } else {
        this.face_error = error?.error?.message || 'Upload or verification failed. Please try again.';
      }
    } finally {
      this.face_loading = false;
    }
  }

  async sendForApproval() {
    if (!this.face_uploaded_path || this.face_approval_loading) return;
    this.face_approval_loading = true;
    try {
      const approval_res = await this.login.face_request_approval(this.face_uploaded_path, this.face_temp_token);
      this.face_pending_data = {
        name: this.face_login_response?.data?.name || this.face_rejected_data?.name,
        request_id: approval_res?.data?.request_id,
        captured_image: this.face_image_base64,
        submitted_at: new Date().toISOString(),
      };
      this.mode = 'face_pending';
      this.face_approval_pending = false;
      this.face_is_resubmission = false;
    } catch (error: any) {
      this.face_error = error?.error?.message || 'Could not submit for approval. Please try again.';
    } finally {
      this.face_approval_loading = false;
    }
  }

  retakeFromApprovalPending() {
    this.face_approval_pending = false;
    this.face_image_base64 = '';
    this.face_uploaded_path = '';
    this.face_error = '';
    setTimeout(() => this.startCamera(), 150);
  }

  async confirmFaceAndProceed() {
    this.show_face_confirm_modal = false;
    await this.completeLogin(this.face_complete_response, this.params?.user_type ?? 'staff');
  }

  cancelFaceConfirm() {
    this.show_face_confirm_modal = false;
  }

  openForgotPassword() {
    this.mode = 'forgot';
    this.forgotStep = 1;
  }

  // STEP 1 : SEND OTP
  async sendOtp() {
    try {
      let payload = { email: this.forgotForm.value.email }
      let create_response = await this.login.send_otp(payload)
      this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      this.forgotStep = 2;
      this.startOtpTimer();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }

  }

  // STEP 2 : VERIFY OTP
  async verifyOtp() {
    try {
      this.otpError = '';
      const otp = this.otpValues.join('');
      if (!otp) {
        this.otpError = 'OTP is required';
        return;
      }

      if (otp.length !== 6) {
        this.otpError = 'Please enter 6 digit OTP';
        return;
      }

      if (this.otpTimer === 0) {
        this.otpError = 'OTP expired. Please resend OTP';
        return;
      }

      const payload = {
        email: this.forgotForm.value.email,
        otp: otp
      };

      let res = await this.login.verify_otp(payload);
      this.gs.toastr_shows_function(res.message, 'Success', 'success');

      this.forgotStep = 3;

    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }



  // STEP 3 : befor login RESET PASSWORD
  async resetPassword() {
    try {
      if (this.forgotForm.invalid) {
        this.forgotForm.markAllAsTouched();
        return;
      }
      if (this.forgotForm.hasError('passwordMismatch')) {
        this.gs.toastr_shows_function('Passwords do not match', 'Error', 'error');
        return;
      }
      let payload = { email: this.forgotForm.value.email, new_password: this.forgotForm.value.new_password }
      let create_response = await this.login.update_password(payload)
      this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      this.backToLogin();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  // after login reset
  async reset() {
    if (this.reset_form.invalid) {
      this.reset_form.markAllAsTouched();
      return;
    }
    if (this.reset_form.hasError('passwordMismatch')) {
      this.gs.toastr_shows_function('Passwords do not match', 'Error', 'error');
      return;
    }
    try {
      let payload = {
        email: this.loginForm.value.email,
        // token: this.gs?.user_data?.token,
        new_password: this.reset_form.value.new_password,
        user_type: this.params.user_type ? this.params.user_type : 'staff'
      }
      let create_response = await this.login.update_password(payload)
      this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      // this.forgotStep = 1;
      // this.backToLogin();
      this.form_modal?.close();
      this.router.navigate(['/sign-in']);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  recaptureAndResubmit() {
    this.face_image_base64 = '';
    this.face_uploaded_path = '';
    this.face_error = '';
    this.face_approval_pending = false;
    this.face_is_resubmission = true;
    this.mode = 'face';
    setTimeout(() => this.startCamera(), 150);
  }

  backToLogin() {
    this.mode = 'login';
    this.face_is_resubmission = false;

    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }
  }

  ngOnDestroy(): void {
    this.route_sub?.unsubscribe();
    this.stopAadhaarPolling();
    this.stopCamera();
    if (this.otpInterval) clearInterval(this.otpInterval);
  }

  onOtpInput(event: any, index: number) {
    const value = event.target.value;

    if (!/^\d$/.test(value)) {
      event.target.value = '';
      return;
    }

    this.otpValues[index] = value;

    // Auto focus next box
    if (index < 5) {
      const next = event.target.nextElementSibling;
      next?.focus();
    }
  }
  onOtpKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpValues[index] && index > 0) {
      const prev = (event.target as HTMLElement).previousElementSibling as HTMLElement;
      prev?.focus();
    }
  }


  startOtpTimer() {
    this.otpTimer = 300;
    this.canResendOtp = false;

    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }

    this.otpInterval = setInterval(() => {
      if (this.otpTimer > 0) {
        this.otpTimer--;
      } else {
        this.canResendOtp = true;
        clearInterval(this.otpInterval);
      }
    }, 1000);
  }
  get face_pending_submitted_at(): string {
    const raw = this.face_pending_data?.submitted_at;
    if (!raw) return '—';
    const d = new Date(raw);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  get otpTime() {
    const min = Math.floor(this.otpTimer / 60);
    const sec = this.otpTimer % 60;
    return `${min}:${sec < 10 ? '0' + sec : sec}`;
  }
  async resendOtp() {
    if (!this.canResendOtp) return;

    try {
      let payload = { email: this.forgotForm.value.email, user_type: this.params.user_type ? this.params.user_type : 'staff' };

      await this.login.send_otp(payload);
      this.gs.toastr_shows_function('OTP resent successfully', 'Success', 'success');

      // Reset OTP boxes
      this.otpValues = ['', '', '', '', '', ''];

      this.startOtpTimer(); // 🔄 Restart timer
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  close_form_modal(): void {
    this.form_modal?.close();
  }

}